import { useState, useEffect, useCallback } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CaptainBadge } from '../components/CaptainBadge';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import EntityAvatar from '../components/EntityAvatar';
import {
  Users, LayoutGrid, Star, Zap, ArrowLeft,
  TrendingUp, Shield, Music, Film, Globe, Gamepad2, Megaphone
} from 'lucide-react';

const CATEGORY_CONFIG = {
  country_leader: { label: 'Rulers', icon: Globe, color: '#5a2d5e', row: 0 },
  ceo: { label: 'CEOs', icon: Shield, color: '#1e3a5f', row: 1 },
  actor: { label: 'Actors', icon: Film, color: '#c97a2e', row: 2 },
  athlete: { label: 'Athletes', icon: Zap, color: '#2d7d5e', row: 3 },
  musician: { label: 'Artists', icon: Music, color: '#b8860b', row: 4 },
  influencer: { label: 'Influencers', icon: Megaphone, color: '#c94070', row: 5 },
};

export default function FormationView({ onBack, onEntityClick, onDraft, teamId: passedTeamId }) {
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // 2026-06 league audit — no blind teams[0] fallback: App injects the
      // selected league's team id, so a missing id means "no team" — render
      // the empty state instead of another league's formation.
      const id = passedTeamId;
      if (id) {
        const res = await api.get(`/teams/${id}/formation`);
        setFormation(res.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [passedTeamId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-4 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;
  }

  if (!formation) {
    return (
      <div className="p-4">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <p className="text-center text-muted-foreground mt-8">No team found</p>
      </div>
    );
  }

  const categories = Object.keys(CATEGORY_CONFIG);

  return (
    <div className="p-4" data-testid="formation-view">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <h2 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
          <LayoutGrid className="h-4 w-4 inline mr-2" />
          Formation View
        </h2>
        <div className="text-right">
          <p className="text-sm font-bold tabular-nums">{formatFranc(formation.total_points)}</p>
          <p className="text-[10px] text-muted-foreground">{formatFranc(formation.budget_remaining)}</p>
        </div>
      </div>

      <ScrollArea className="h-[calc(100vh-120px)]">
        <div className="space-y-4">
          {categories.map(cat => {
            const config = CATEGORY_CONFIG[cat];
            const Icon = config.icon;
            const entities = formation.formation?.[cat] || [];
            const emptySlots = Math.max(0, 3 - entities.length);

            return (
              <div key={cat} className="bg-card border border-border/60 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-6 w-6 rounded-full flex items-center justify-center" style={{ backgroundColor: config.color }}>
                    <Icon className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="text-sm font-semibold">{config.label}</span>
                  <Badge variant="secondary" className="text-[9px] ml-auto">
                    {entities.length}/3
                  </Badge>
                </div>

                <div className="flex gap-3 overflow-x-auto pb-1">
                  {entities.map(slot => {
                    const entity = slot.entity;
                    return (
                      <div
                        key={slot.entity_id}
                        className="flex flex-col items-center min-w-[80px] cursor-pointer"
                        onClick={() => onEntityClick?.(slot.entity_id)}
                      >
                        <div className="relative">
                          <EntityAvatar entity={entity || { entity_type: cat }} size={48} />
                          {slot.is_captain && <CaptainBadge className="-top-1 -right-1" />}
                        </div>
                        <p className="text-[10px] font-medium mt-1 text-center truncate max-w-[75px]">
                          {entity?.name || '?'}
                        </p>
                        <p className="text-[9px] text-muted-foreground tabular-nums">
                          {formatFranc(entity?.pricing?.price)}
                        </p>
                        {entity?.stats?.weekly_points > 0 && (
                          <span className="badge-positive text-[8px] px-1 py-0 rounded-full">
                            {formatFranc(entity.stats.weekly_points, { sign: true })}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Empty slots */}
                  {Array.from({ length: emptySlots }).map((_, i) => (
                    <div
                      key={`${cat}-empty-${i}`}
                      className="flex flex-col items-center min-w-[80px] cursor-pointer"
                      onClick={() => onDraft?.()}
                    >
                      <div
                        className="h-12 w-12 rounded-full border-2 border-dashed border-border flex items-center justify-center"
                      >
                        <span className="text-lg text-muted-foreground">+</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">Draft</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
